import React, { ComponentProps, RefObject, useCallback, useEffect, useRef } from 'react';

import {
  useTrackProductClick,
  useTrackProductImpression,
} from '../../hooks/tracking/useTrackingEvents';

interface IProductImpression<TComponent extends React.ElementType> {
  Component?: TComponent | 'div';
  list?: string;
  position: number;
  product: ITrackableProduct;
  root?: RefObject<HTMLElement> | null;
}

const ProductImpression = <TComponent extends React.ElementType>({
  Component = 'div',
  list,
  position,
  product,
  root,
  ...props
}: IProductImpression<TComponent> & ComponentProps<TComponent>) => {
  const ref = useRef<HTMLDivElement>(null);
  const trackProductImpression = useTrackProductImpression(list, position);
  const trackProductClick = useTrackProductClick(list, position);

  useEffect(() => {
    if (list && ref.current) {
      const observer = new IntersectionObserver(
        (entries) => {
          if (entries[0].isIntersecting) {
            observer.disconnect();
            trackProductImpression(product);
          }
        },
        {
          root: root?.current || null,
          threshold: 0.5,
        },
      );

      observer.observe(ref.current);

      return () => {
        observer.disconnect();
      };
    }
  }, [list, product, trackProductImpression]);

  const onClick = useCallback(() => {
    if (props.onClick) {
      props.onClick();
    }

    if (list) {
      trackProductClick(product);
    }
  }, [list, product, trackProductClick, props.onClick]);

  const onSelect = useCallback(() => {
    if (props.onSelect) {
      props.onSelect();
    }

    if (list) {
      trackProductClick(product);
    }
  }, [list, product, trackProductClick, props.onSelect]);

  return <Component {...props} ref={ref} onClick={onClick} onSelect={onSelect} />;
};

export default ProductImpression;
